import { useQuery, useQueryClient } from '@tanstack/react-query';
import { sessionScopeKey, useSessionScopeFence } from '@/hooks/useSessionScope';
import type { RequestOptions } from '@/services/apiClient';

export const UNIFIED_CRM_READ = 'crm:unified:read';
export function useCrmRead<T>(
  key: readonly unknown[],
  read: (options: RequestOptions) => Promise<T>,
  enabled = true,
  options: { freshOnMount?: boolean } = {},
) {
  const scope = useSessionScopeFence(UNIFIED_CRM_READ);
  const freshOnMount = options.freshOnMount === true;
  const query = useQuery({
    queryKey: ['unified-crm', ...sessionScopeKey(scope.identity), ...key],
    queryFn: ({ signal }) => read({ signal, sessionOrigin: scope.identity.sessionOrigin }),
    enabled: scope.enabled && enabled,
    staleTime: freshOnMount ? 0 : 15_000,
    ...(freshOnMount
      ? { gcTime: 0, refetchOnMount: 'always' as const, refetchOnWindowFocus: 'always' as const }
      : {}),
    // Evidence reads append intentional-access audit receipts. Idle visibility must not poll them.
    refetchInterval: freshOnMount ? false : 10_000,
    refetchIntervalInBackground: false,
    retry: false,
  });
  const readable =
    !freshOnMount ||
    (scope.enabled &&
      scope.isCurrent(scope.identity) &&
      query.isFetchedAfterMount &&
      !query.isFetching &&
      !query.isError);
  return {
    ...query,
    scope,
    data: readable ? query.data : undefined,
    isLoading:
      query.isLoading ||
      (freshOnMount &&
        scope.enabled &&
        !query.isError &&
        (!query.isFetchedAfterMount || query.isFetching)),
  };
}
export function useCrmInvalidation() {
  const client = useQueryClient();
  return () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ['unified-crm'] }),
      client.invalidateQueries({ queryKey: ['crm'] }),
      client.invalidateQueries({ queryKey: ['crm-depth'] }),
    ]);
}
